// src/utils/storage.ts
// Versioned, scoped localStorage wrapper to prevent Inkwell-style persistence issues

import { logger } from "@/lib/logger";

const log = logger.child("Storage");

const APP_VERSION = "0.4.0"; // bump when you want to invalidate all local state

// Environment key to prevent cross-environment storage collisions
// Uses explicit env var if set, otherwise derives from host
function getEnvKey(): string {
  if (typeof window === "undefined") return "ssr";
  const explicit = import.meta.env.VITE_ENV_KEY;
  if (explicit) return explicit;
  // Sanitize host for use in storage key (replace : and . with _)
  return window.location.host.replace(/[:.]/g, "_");
}

type StorageScope = {
  userId?: string | null;
  clientId?: string | null;
};

function buildKey(baseKey: string, scope?: StorageScope): string {
  const env = getEnvKey();
  const user = scope?.userId ?? "anon";
  const client = scope?.clientId ?? "none";
  return `nexus:${env}:${APP_VERSION}:${user}:${client}:${baseKey}`;
}

/**
 * Get a scoped item from localStorage.
 * Automatically namespaced by app version, user, and client.
 */
export function getScopedItem<T>(key: string, scope?: StorageScope): T | null {
  if (typeof window === "undefined") return null;

  try {
    const fullKey = buildKey(key, scope);
    const raw = window.localStorage.getItem(fullKey);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Set a scoped item in localStorage.
 * Automatically namespaced by app version, user, and client.
 */
export function setScopedItem<T>(
  key: string,
  value: T,
  scope?: StorageScope,
): void {
  if (typeof window === "undefined") return;

  try {
    const fullKey = buildKey(key, scope);
    window.localStorage.setItem(fullKey, JSON.stringify(value));
  } catch {
    // swallow for safety
  }
}

/**
 * Remove a scoped item from localStorage.
 */
export function removeScopedItem(key: string, scope?: StorageScope): void {
  if (typeof window === "undefined") return;

  try {
    const fullKey = buildKey(key, scope);
    window.localStorage.removeItem(fullKey);
  } catch {
    // ignore
  }
}

/**
 * Clear all localStorage items for the current app version.
 * This is the "nuclear option" for cleaning up stale state.
 */
export function clearAllVersionedStorage(): void {
  if (typeof window === "undefined") return;

  try {
    const env = getEnvKey();
    const prefix = `nexus:${env}:${APP_VERSION}:`;
    const keysToRemove: string[] = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

/**
 * Remove keys written under a PREVIOUS app version.
 *
 * `clearAllVersionedStorage` only clears the current `APP_VERSION` prefix, so a
 * version bump orphans older keys: invisible to the app, and exempt from the
 * logout purge that would otherwise have removed them. Bumping the version
 * therefore preserved data instead of retiring it — the opposite of what the
 * mechanism looks like it does.
 *
 * Returns the number of keys removed.
 */
export function purgeOrphanedVersionKeys(): number {
  if (typeof window === "undefined") return 0;

  try {
    const env = getEnvKey();
    const currentPrefix = `nexus:${env}:${APP_VERSION}:`;
    const anyVersionPrefix = `nexus:${env}:`;
    const keysToRemove: string[] = [];

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(anyVersionPrefix) && !key.startsWith(currentPrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    return keysToRemove.length;
  } catch {
    return 0;
  }
}

/**
 * Dev helper: attach window.nexusDev utilities for debugging storage.
 * Only call this in development mode.
 */
export function attachDevReset(): void {
  if (typeof window === "undefined") return;

  (window as any).nexusDev = {
    ...((window as any).nexusDev || {}),

    // Clear all versioned storage and reload
    resetLocal: () => {
      clearAllVersionedStorage();
      window.location.reload();
    },

    // Get all nexus storage keys
    getAllKeys: () => {
      return Object.keys(localStorage).filter((k) => k.includes("nexus:"));
    },

    // Get keys grouped by version
    getKeysByVersion: () => {
      const keys = Object.keys(localStorage).filter((k) =>
        k.startsWith("nexus:"),
      );
      const grouped: Record<string, string[]> = {};

      keys.forEach((key) => {
        const parts = key.split(":");
        const version = parts[1] || "unknown";
        if (!grouped[version]) grouped[version] = [];
        grouped[version].push(key);
      });

      return grouped;
    },

    // Get current storage scope
    getScope: () => {
      const env = getEnvKey();
      // Try to extract from any scoped key
      const keys = Object.keys(localStorage).filter((k) =>
        k.startsWith(`nexus:${env}:${APP_VERSION}:`),
      );
      if (keys.length === 0)
        return { env, version: APP_VERSION, userId: null, clientId: null };

      const firstKey = keys[0];
      const parts = firstKey.split(":");
      return {
        env: parts[1] || env,
        version: parts[2] || APP_VERSION,
        userId: parts[3] || null,
        clientId: parts[4] || null,
      };
    },

    // Get storage size (approximate)
    getStorageSize: () => {
      const keys = Object.keys(localStorage).filter((k) =>
        k.includes("nexus:"),
      );
      let totalSize = 0;

      keys.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value) {
          totalSize += key.length + value.length;
        }
      });

      const kb = (totalSize / 1024).toFixed(2);
      return `${kb} KB (${keys.length} keys)`;
    },

    // Pretty print all nexus storage
    inspectStorage: () => {
      const keys = Object.keys(localStorage).filter((k) =>
        k.includes("nexus:"),
      );
      const data: Record<string, any> = {};

      keys.forEach((key) => {
        try {
          const value = localStorage.getItem(key);
          data[key] = value ? JSON.parse(value) : null;
        } catch {
          data[key] = localStorage.getItem(key);
        }
      });

      log.debug("Storage inspection", {
        keys: keys.map((key) => ({
          key,
          size: `${((key.length + (localStorage.getItem(key)?.length || 0)) / 1024).toFixed(2)} KB`,
        })),
      });

      return data;
    },

    // Clear ALL app data (all versions) - useful for demo cleanup
    clearAllAppData: () => {
      const keys = Object.keys(localStorage).filter((k) =>
        k.startsWith("nexus:"),
      );
      log.debug("Clearing all storage keys", { count: keys.length });
      keys.forEach((key) => localStorage.removeItem(key));
      log.debug("All Nexus app data cleared. Reloading...");
      window.location.reload();
    },

    // Clear data for a specific client ID
    clearClientData: (clientId: string) => {
      const keys = Object.keys(localStorage).filter(
        (k) => k.startsWith("nexus:") && k.includes(`:${clientId}:`),
      );
      log.debug("Clearing keys for client", { count: keys.length, clientId });
      keys.forEach((key) => localStorage.removeItem(key));
      log.debug("Client data cleared", { clientId });
      return keys;
    },

    // Full demo reset - clear all storage + IndexedDB + caches
    fullDemoReset: async () => {
      log.debug("Starting full demo reset...");

      // Clear localStorage
      const keys = Object.keys(localStorage).filter((k) =>
        k.startsWith("nexus:"),
      );
      keys.forEach((key) => localStorage.removeItem(key));
      log.debug("Cleared localStorage keys", { count: keys.length });

      // Clear sessionStorage
      const sessionKeys = Object.keys(sessionStorage).filter((k) =>
        k.includes("nexus"),
      );
      sessionKeys.forEach((key) => sessionStorage.removeItem(key));
      log.debug("Cleared sessionStorage keys", { count: sessionKeys.length });

      // Clear IndexedDB databases with "nexus" in name
      if (window.indexedDB && window.indexedDB.databases) {
        try {
          const databases = await window.indexedDB.databases();
          for (const db of databases) {
            if (db.name && db.name.toLowerCase().includes("nexus")) {
              window.indexedDB.deleteDatabase(db.name);
              log.debug("Deleted IndexedDB", { dbName: db.name });
            }
          }
        } catch (e) {
          log.debug("Could not clear IndexedDB", { error: e });
        }
      }

      log.debug("Full demo reset complete. Reloading...");
      window.location.reload();
    },
  };

  // Log available commands
  log.debug(
    "Storage utilities available: resetLocal, getAllKeys, getKeysByVersion, getScope, getStorageSize, inspectStorage, clearAllAppData, clearClientData(id), fullDemoReset",
  );
}
